import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";
import { EmptyState } from "@/components/shared/EmptyState";
import { SkeletonCard } from "@/components/ui/Skeleton";
import { UserMinus } from "lucide-react";
import {
  listInstances,
  type LifecycleInstance,
  type LifecycleInstanceStatus,
} from "@/api/lifecycle";
import { listEmployees } from "@/api/employees";
import type { EmployeeListItem } from "@/types/employee";

type Filter = "active" | "completed" | "all";

const STATUS_BADGE: Record<
  LifecycleInstanceStatus,
  { variant: "success" | "warning" | "default" | "danger"; label: string }
> = {
  IN_PROGRESS: { variant: "warning", label: "In progress" },
  COMPLETED: { variant: "success", label: "Completed" },
  CANCELLED: { variant: "default", label: "Cancelled" },
};

export function OffboardingListPage() {
  const [instances, setInstances] = useState<LifecycleInstance[] | null>(null);
  const [employees, setEmployees] = useState<Record<string, EmployeeListItem>>({});
  const [filter, setFilter] = useState<Filter>("active");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // ``Promise.allSettled`` so that a partial failure (e.g. employee
      // directory 403 for a non-HR viewer, or the employee list endpoint
      // being slow/unavailable) never leaves ``instances`` null — which
      // is the state that renders the skeleton indefinitely. Each
      // branch falls back to an empty result and we still paint the
      // page rather than spinning forever.
      const [instRes, empRes] = await Promise.allSettled([
        listInstances(),
        listEmployees(1, 100),
      ]);
      if (cancelled) return;

      if (instRes.status === "fulfilled") {
        // Only offboarding shows here; onboarding has its own Pilot UI.
        setInstances(instRes.value.filter((i) => i.event === "OFFBOARDING"));
      } else {
        console.error("OffboardingListPage: listInstances failed", instRes.reason);
        setInstances([]);
      }

      if (empRes.status === "fulfilled") {
        const map: Record<string, EmployeeListItem> = {};
        for (const e of empRes.value.items) map[e.id] = e;
        setEmployees(map);
      } else {
        console.warn(
          "OffboardingListPage: listEmployees failed (names will be blank)",
          empRes.reason,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!instances) return [];
    if (filter === "active")
      return instances.filter((i) => i.status === "IN_PROGRESS");
    if (filter === "completed")
      return instances.filter((i) => i.status === "COMPLETED");
    return instances;
  }, [instances, filter]);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Offboarding"
        subtitle={
          instances
            ? `${instances.filter((i) => i.status === "IN_PROGRESS").length} active`
            : "Loading…"
        }
      />

      <div className="mb-4">
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
          options={[
            { value: "active", label: "Active" },
            { value: "completed", label: "Completed" },
            { value: "all", label: "All" },
          ]}
        />
      </div>

      {!instances ? (
        <div className="space-y-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={UserMinus}
          title="No offboardings"
          description={
            filter === "active"
              ? "No one is currently being offboarded."
              : "Nothing matches this filter yet."
          }
        />
      ) : (
        <div className="space-y-2">
          {filtered.map((inst) => {
            const emp = employees[inst.employee_id];
            const pct =
              inst.total_tasks === 0
                ? 0
                : Math.round((inst.completed_tasks / inst.total_tasks) * 100);
            const badge = STATUS_BADGE[inst.status];
            return (
              <Link
                key={inst.id}
                to={`/offboarding/${inst.id}`}
                className="block"
              >
                <Card className="hover:border-accent/50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-text-primary">
                          {emp
                            ? `${emp.first_name} ${emp.last_name}`
                            : "Employee"}
                        </p>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </div>
                      <p className="text-xs text-text-muted mt-1">
                        {emp?.title || ""}
                        {emp?.department?.name ? ` · ${emp.department.name}` : ""}
                      </p>
                    </div>
                    <div className="w-40">
                      <div className="flex justify-between text-xs text-text-muted mb-1">
                        <span>
                          {inst.completed_tasks} / {inst.total_tasks} tasks
                        </span>
                        <span>{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-border rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-xs text-text-muted w-28 text-right">
                      Started{" "}
                      {new Date(inst.started_at).toLocaleDateString()}
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
